// campaign-sessions/index.ts — public surface for the campaign-sessions bundle.
//
// Output is an IIFE (build-campaign-sessions-bundle.mjs) loaded as a footer <script> AFTER template.js,
// so the template.js / core-helpers globals it calls (queryData / hideEl / showEl / iconMap / BASE_URL /
// getElementValue / escapeHtml) resolve at call time.
//
// Renders the campaign-sessions list (Upcoming + Past) from the JSON island emitted by
// ft_campaign_sessions_mount() on both tools/campaign_sessions/campaign_sessions.php (view) and
// modify_campaign_sessions.php (manage). "Add Session" posts to the insert endpoint, which returns the
// new row as JSON; the row is spliced into the list client-side (no full-page refresh, no re-list).
//
// Public window globals (all runtime-invoked):
//   ftMountCampaignSessions(root?)  — scanner; self-runs on load + can be re-called after AJAX.
//   ftCampaignSessionAdd()          — the modify page's "Add Session" action.
//   ftCampaignSessionsInsert(s,b)   — splice a row in (used by ftCampaignSessionAdd; exported for reuse).
//   ftCampaignSessionsRemove(id)    — drop a row and renumber (used by the row delete; exported for reuse).
//
// Build via: npm run build:campaign-sessions

import {
	ftMountCampaignSessions,
	ftCampaignSessionsInsert,
	ftCampaignSessionsRemove,
	type CsSession,
} from './render-campaign-sessions';

declare global {
	interface Window {
		ftMountCampaignSessions?: (root?: Element | Document) => void;
		ftCampaignSessionAdd?: () => Promise<void>;
		ftCampaignSessionsInsert?: (s: CsSession, b: 'future' | 'past') => void;
		ftCampaignSessionsRemove?: (id: string) => void;
	}
}

function escapeText(s: unknown): string {
	return (typeof escapeHtml === 'function') ? escapeHtml(String(s ?? '')) : String(s ?? '');
}

function readValue(id: string): string {
	if (typeof getElementValue === 'function') {
		const v = getElementValue(id);
		return (v === null || v === undefined) ? '' : String(v);
	}
	const el = document.getElementById(id) as HTMLInputElement | null;
	return el ? String(el.value ?? '') : '';
}

function setRefresh(html: string): void {
	const refresh = document.getElementById('div_refresh');
	if (refresh) refresh.innerHTML = html;
}

// "Add Session" handler for the GM modify page. Replaces the old two-step queryDataInputs (insert,
// then re-list everything). Posts the form, then splices the returned row into the live list.
async function ftCampaignSessionAdd(): Promise<void> {
	const campaign_id = readValue('input_hidden_campaign_id');
	const campaign_session_date = readValue('campaign_session_date');
	const campaign_session_time = readValue('campaign_session_time');
	const notify_players = readValue('notify_players');

	if (!campaign_session_date || !campaign_session_time) {
		setRefresh('<div class="alert alert-warning">Please choose a date and time.</div>');
		return;
	}

	setRefresh(typeof loader !== 'undefined' ? loader : '');

	try {
		const res = await fetch(BASE_URL + 'options/tools/campaign_sessions/insert_session_inc.php', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ campaign_id, campaign_session_date, campaign_session_time, notify_players }),
		});
		const out = await res.json();

		if (out && out.status === 'success' && out.session) {
			const session = out.session as CsSession;
			ftCampaignSessionsInsert(session, out.bucket === 'past' ? 'past' : 'future');
			const when = [session.weekday_f, [session.month_f, session.day_f].filter(Boolean).join(' '), session.time_f].filter(Boolean).join(' · ');
			setRefresh('<div class="alert alert-success">' + escapeText(out.message || 'Session added.') + (when ? ' <span class="ft-cs-status-when">' + escapeText(when) + '</span>' : '') + '</div>');
			// Intentionally leave the date/time inputs as-is so the GM can quickly add more dates
			// without re-entering everything.
		} else {
			setRefresh('<div class="alert alert-warning">' + escapeText((out && out.message) || 'Could not add the session.') + '</div>');
		}
	} catch (e) {
		console.error('[campaign-sessions] add failed', e);
		setRefresh('<div class="alert alert-danger">Something went wrong adding the session. Please try again.</div>');
	}
}

Object.assign(window, {
	ftMountCampaignSessions,
	ftCampaignSessionAdd,
	ftCampaignSessionsInsert,
	ftCampaignSessionsRemove,
});

// Footer <script> runs after the mount div above it is parsed — render immediately, with a
// DOMContentLoaded fallback for any mount parsed later. The data-ft-cs-done guard makes re-scans a no-op.
ftMountCampaignSessions();
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => ftMountCampaignSessions());
}

document.addEventListener('click', (event) => {
	const button = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>('[data-ft-cs-add]');
	if (!button || button.disabled) return;
	button.disabled = true; // no double-submit while the insert is in flight
	void ftCampaignSessionAdd().finally(() => { button.disabled = false; });
});

export {};
